import React, { useState, useRef, useEffect } from 'react';

const NumberDropdown = ({ onToggle, isOtherOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    code: 'US',
    name: 'United States',
    flag: 'https://flagcdn.com/w20/us.png',
    dialCode: '+1',
  });
  const dropdownRef = useRef(null);

  const countries = [
    {
      code: 'US',
      name: 'United States',
      flag: 'https://flagcdn.com/w20/us.png',
      dialCode: '+1',
    },
    {
      code: 'CA',
      name: 'Canada',
      flag: 'https://flagcdn.com/w20/ca.png',
      dialCode: '+1',
    },
    {
      code: 'GB',
      name: 'United Kingdom',
      flag: 'https://flagcdn.com/w20/gb.png',
      dialCode: '+44',
    },
    {
      code: 'DE',
      name: 'Germany',
      flag: 'https://flagcdn.com/w20/de.png',
      dialCode: '+49',
    },
    {
      code: 'FR',
      name: 'France',
      flag: 'https://flagcdn.com/w20/fr.png',
      dialCode: '+33',
    },
    {
      code: 'IT',
      name: 'Italy',
      flag: 'https://flagcdn.com/w20/it.png',
      dialCode: '+39',
    },
    {
      code: 'ES',
      name: 'Spain',
      flag: 'https://flagcdn.com/w20/es.png',
      dialCode: '+34',
    },
    {
      code: 'AU',
      name: 'Australia',
      flag: 'https://flagcdn.com/w20/au.png',
      dialCode: '+61',
    },
    {
      code: 'JP',
      name: 'Japan',
      flag: 'https://flagcdn.com/w20/jp.png',
      dialCode: '+81',
    },
    {
      code: 'CN',
      name: 'China',
      flag: 'https://flagcdn.com/w20/cn.png',
      dialCode: '+86',
    },
    {
      code: 'IN',
      name: 'India',
      flag: 'https://flagcdn.com/w20/in.png',
      dialCode: '+91',
    },
    {
      code: 'BR',
      name: 'Brazil',
      flag: 'https://flagcdn.com/w20/br.png',
      dialCode: '+55',
    },
    {
      code: 'RU',
      name: 'Russia',
      flag: 'https://flagcdn.com/w20/ru.png',
      dialCode: '+7',
    },
    {
      code: 'MX',
      name: 'Mexico',
      flag: 'https://flagcdn.com/w20/mx.png',
      dialCode: '+52',
    },
    {
      code: 'NL',
      name: 'Netherlands',
      flag: 'https://flagcdn.com/w20/nl.png',
      dialCode: '+31',
    },
  ];

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    onToggle && onToggle('number', false);
  };

  const handleToggle = () => {
    if (isOtherOpen) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle && onToggle('number', newState);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        onToggle && onToggle('number', false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Close this dropdown if other dropdown opens
  useEffect(() => {
    if (isOtherOpen && isOpen) {
      setIsOpen(false);
    }
  }, [isOtherOpen, isOpen]);

  return (
    <div className="relative min-h-full bg-[#0000001A]" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="flex items-center justify-center gap-1 min-h-full px-1 py-2 w-[65px] sm:w-[62px]"
      >
        <img
          src={selectedCountry.flag}
          alt={selectedCountry.name}
          className="w-4 h-4 rounded-full object-cover"
        />
        <span className="text-[#808080] font-normal text-[12px] leading-[19px]">
          {selectedCountry.dialCode}
        </span>
        <svg
          width="12"
          height="13"
          className={`min-w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.5 4.75L6 8.25L2.5 4.75"
            stroke="#535353"
            strokeWidth="1.125"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto">
          <div className="py-2">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 transition duration-200 ${
                  selectedCountry.code === country.code ? 'bg-gray-50' : ''
                }`}
              >
                <img
                  src={country.flag}
                  alt={`${country.name} flag`}
                  className="w-5 h-5 rounded-full object-cover"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {country.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {country.dialCode}
                  </div>
                </div>
                {selectedCountry.code === country.code && (
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NumberDropdown;
